/**
 * Class content downloader — packages a class (or a whole batch) as one zip.
 *
 * Scope, deliberately narrow: only files we host. Tests and assessments are
 * test-ids rather than artifacts and external videos live on somebody else's
 * server, so both are written into the manifest as references and never
 * fetched. See classAssets.ts, which decides that split for both this and the
 * counts shown on the class card.
 *
 * Folder layout inside the zip:
 *
 *   <Batch or Class>/
 *     <Class name (date)>/            <- only when exporting a whole batch
 *       01-Pre-Class/Reading-Material/notes.pdf
 *       02-Live-Class/Recordings/session-1.mp4
 *       03-Post-Class/Videos/summary.mp4
 *       class-manifest.json
 *     batch-manifest.json
 *
 * Memory: the archive is assembled in the tab, so peak usage is roughly the
 * size of everything selected. estimateBundle() exists so the caller can warn
 * before starting rather than discovering it when the tab dies — recordings
 * are the case that gets big fast.
 */

import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { ClassConfig, SectionType } from '@/pages/superadmin-new/components/ClassManagement/LessonPlanner/types/lessonPlannerTypes';
import {
  ClassAsset,
  ClassAssetSummary,
  SECTION_FOLDER,
  sanitizeName,
  summarizeClassAssets,
} from '@/pages/superadmin-new/components/ClassManagement/LessonPlanner/utils/classAssets';

/** Above this, a browser-side zip is a coin flip; the caller should say so. */
export const BROWSER_ZIP_WARN_BYTES = 1.5 * 1024 * 1024 * 1024;

/** How many files are fetched at once. Three keeps the CDN happy and progress readable. */
const CONCURRENCY = 3;

/**
 * Chunks arrive every few kilobytes. Reporting each one would re-render the
 * panel thousands of times a second and starve the download itself, so byte
 * progress is coalesced onto this interval.
 */
const PROGRESS_INTERVAL_MS = 200;

export interface BundleEntry {
  classConfig: ClassConfig;
  summary: ClassAssetSummary;
  /** Folder name for this class inside the zip. */
  folder: string;
}

export interface BundleEstimate {
  classes: number;
  files: number;
  knownBytes: number;
  hasUnknownSizes: boolean;
  externalLinks: number;
  nonFiles: number;
  /** True when the estimate alone is enough to expect trouble in the browser. */
  isLarge: boolean;
}

export interface DownloadProgress {
  /** Files finished (succeeded or failed). */
  completed: number;
  total: number;
  /** Bytes pulled so far across all files. */
  bytes: number;
  /** What is being fetched right now. */
  currentFile: string;
  phase: 'downloading' | 'zipping' | 'done';
}

export interface DownloadResult {
  fileName: string;
  filesWritten: number;
  /** Files we could not fetch, with the reason. The zip still ships without them. */
  failures: { title: string; path: string; reason: string }[];
  bytes: number;
}

/**
 * Name a class folder as "<title> (yyyy-mm-dd)" so a batch export sorts and
 * reads sensibly even when two classes share a title.
 */
export const classFolderName = (classConfig: ClassConfig): string => {
  const title = sanitizeName(classConfig.title || 'Class');
  const raw = classConfig.scheduledDate;
  const date = raw ? new Date(raw) : null;
  if (!date || Number.isNaN(date.getTime())) return title;
  const stamp = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  return `${title} (${stamp})`;
};

/** Build the entry list for one or many classes, skipping classes with nothing in them. */
export const buildBundle = (classConfigs: ClassConfig[]): BundleEntry[] =>
  classConfigs
    .map((classConfig) => ({
      classConfig,
      summary: summarizeClassAssets(classConfig),
      folder: classFolderName(classConfig),
    }))
    .filter((entry) => entry.summary.downloadable.length > 0);

export const estimateBundle = (entries: BundleEntry[]): BundleEstimate => {
  const estimate: BundleEstimate = {
    classes: entries.length,
    files: 0,
    knownBytes: 0,
    hasUnknownSizes: false,
    externalLinks: 0,
    nonFiles: 0,
    isLarge: false,
  };

  for (const entry of entries) {
    estimate.files += entry.summary.downloadable.length;
    estimate.knownBytes += entry.summary.knownBytes;
    estimate.externalLinks += entry.summary.external.length;
    estimate.nonFiles += entry.summary.nonFiles.length;
    if (entry.summary.hasUnknownSizes) estimate.hasUnknownSizes = true;
  }

  estimate.isLarge = estimate.knownBytes > BROWSER_ZIP_WARN_BYTES;
  return estimate;
};

/** Path of an asset inside its class folder. */
const assetPath = (asset: ClassAsset): string =>
  `${SECTION_FOLDER[asset.section as SectionType]}/${asset.group}/${asset.fileName}`;

/**
 * Two files in one folder can legitimately carry the same name — two
 * recordings of the same class, say. JSZip would silently keep only the last,
 * so number the repeats instead.
 */
const uniquePath = (path: string, taken: Set<string>): string => {
  if (!taken.has(path)) {
    taken.add(path);
    return path;
  }
  const dot = path.lastIndexOf('.');
  const stem = dot > 0 ? path.slice(0, dot) : path;
  const ext = dot > 0 ? path.slice(dot) : '';
  let n = 2;
  let candidate = `${stem}-${n}${ext}`;
  while (taken.has(candidate)) {
    n += 1;
    candidate = `${stem}-${n}${ext}`;
  }
  taken.add(candidate);
  return candidate;
};

/**
 * Fetch one file, reporting bytes as they arrive.
 *
 * Read through the stream rather than calling response.blob() so a 700 MB
 * recording shows movement instead of a frozen progress bar, and so an abort
 * actually stops the transfer.
 */
const fetchWithProgress = async (
  url: string,
  signal: AbortSignal,
  onBytes: (delta: number) => void
): Promise<Blob> => {
  const response = await fetch(url, { signal, credentials: 'omit' });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);

  if (!response.body) {
    // Older browsers with no streaming body: fall back to a plain blob read.
    const blob = await response.blob();
    onBytes(blob.size);
    return blob;
  }

  const reader = response.body.getReader();
  const chunks: BlobPart[] = [];
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    if (value) {
      chunks.push(value);
      onBytes(value.byteLength);
    }
  }
  return new Blob(chunks);
};

/** The human-readable index that ships inside every class folder. */
const buildClassManifest = (entry: BundleEntry, paths: Map<ClassAsset, string>) => ({
  class: entry.classConfig.title,
  scheduled: entry.classConfig.scheduledDate,
  instructor: entry.classConfig.instructorName || null,
  exported_at: new Date().toISOString(),
  files: entry.summary.downloadable.map((asset) => ({
    title: asset.title,
    path: paths.get(asset) || assetPath(asset),
    section: asset.section,
    kind: asset.group,
    source: asset.isRecording ? 'teams-recording' : 'uploaded',
  })),
  /** Not downloaded — hosted elsewhere. Open these links directly. */
  external_links: entry.summary.external.map((ref) => ({
    title: ref.title,
    section: ref.section,
    url: ref.url,
  })),
  /** Not downloaded — these are activities in the platform, not files. */
  tests_and_assessments: entry.summary.nonFiles.map((ref) => ({
    title: ref.title,
    kind: ref.kind,
    section: ref.section,
  })),
});

/**
 * Download every hosted file in `entries` and hand the user one zip.
 *
 * A file that fails is recorded and skipped, never fatal: one dead CDN link
 * should not cost the teacher the other forty files.
 */
export const downloadClassBundle = async (
  entries: BundleEntry[],
  zipName: string,
  options: {
    /** Set when more than one class is in the archive: adds the per-class folder level. */
    nestByClass: boolean;
    signal: AbortSignal;
    onProgress: (progress: DownloadProgress) => void;
  }
): Promise<DownloadResult> => {
  const { nestByClass, signal, onProgress } = options;

  const zip = new JSZip();
  const failures: DownloadResult['failures'] = [];
  const total = entries.reduce((sum, entry) => sum + entry.summary.downloadable.length, 0);

  let completed = 0;
  let bytes = 0;
  let filesWritten = 0;
  let lastReportAt = 0;

  const report = (
    currentFile: string,
    phase: DownloadProgress['phase'] = 'downloading',
    /** File-level events always report; mid-transfer byte updates are coalesced. */
    force = true
  ) => {
    const now = Date.now();
    if (!force && now - lastReportAt < PROGRESS_INTERVAL_MS) return;
    lastReportAt = now;
    onProgress({ completed, total, bytes, currentFile, phase });
  };

  report('', 'downloading');

  for (const entry of entries) {
    const root = nestByClass ? `${entry.folder}/` : '';
    const taken = new Set<string>();
    // Keyed by the asset itself, not its id: a recording and a content item
    // can carry the same numeric id within one class, and keying by id would
    // quietly collapse the two onto one path.
    const paths = new Map<ClassAsset, string>();

    // Reserve every path up front so the manifest and the archive agree even
    // when a later fetch fails and the file never lands.
    for (const asset of entry.summary.downloadable) {
      paths.set(asset, uniquePath(assetPath(asset), taken));
    }

    const queue = [...entry.summary.downloadable];

    const worker = async () => {
      for (;;) {
        if (signal.aborted) return;
        const asset = queue.shift();
        if (!asset) return;

        const path = paths.get(asset) as string;
        report(asset.title);

        try {
          const blob = await fetchWithProgress(asset.url, signal, (delta) => {
            bytes += delta;
            report(asset.title, 'downloading', false);
          });
          // Videos and PDFs are already compressed; STORE skips a deflate pass
          // that would cost minutes and save nothing.
          zip.file(`${root}${path}`, blob, { compression: 'STORE' });
          filesWritten += 1;
        } catch (err: any) {
          if (signal.aborted) return;
          failures.push({
            title: asset.title,
            path,
            reason: err?.message || 'Download failed',
          });
        } finally {
          completed += 1;
          report(asset.title);
        }
      }
    };

    await Promise.all(
      Array.from({ length: Math.min(CONCURRENCY, queue.length) }, () => worker())
    );

    if (signal.aborted) throw new DOMException('Aborted', 'AbortError');

    zip.file(
      `${root}class-manifest.json`,
      JSON.stringify(buildClassManifest(entry, paths), null, 2)
    );
  }

  if (nestByClass) {
    zip.file(
      'batch-manifest.json',
      JSON.stringify(
        {
          exported_at: new Date().toISOString(),
          classes: entries.map((entry) => ({
            class: entry.classConfig.title,
            folder: entry.folder,
            scheduled: entry.classConfig.scheduledDate,
            files: entry.summary.downloadable.length,
            recordings: entry.summary.recordings,
            videos: entry.summary.videos,
            reading_material: entry.summary.documents,
            tests: entry.summary.tests,
            assessments: entry.summary.assessments,
          })),
        },
        null,
        2
      )
    );
  }

  if (failures.length) {
    zip.file(
      'skipped-files.txt',
      [
        'These files could not be downloaded and are not in this archive.',
        'Their entries are still listed in the manifests.',
        '',
        ...failures.map((f) => `${f.path}  —  ${f.reason}`),
      ].join('\n')
    );
  }

  report('', 'zipping');
  const archive = await zip.generateAsync({ type: 'blob', compression: 'STORE' });

  if (signal.aborted) throw new DOMException('Aborted', 'AbortError');

  saveAs(archive, `${sanitizeName(zipName)}.zip`);
  report('', 'done');

  return { fileName: `${sanitizeName(zipName)}.zip`, filesWritten, failures, bytes };
};
